import { useEffect, useState } from "react";

import dayjs from "dayjs";

import Form from "react-bootstrap/Form";

import { useApi } from "@/hooks/useApi";
import { useSwal } from "@/hooks/useSwal";
import { DischargeType } from "@/enums/DischargeType";
import { transformData } from "@/utils/transformData";
import { attendanceMap } from "@/utils/mappings/attendance";

import { ListOrFormControlUpdate } from "@/components/HospitalizationPermits/ReportCard/ListOrFormControlUpdate";
import { StatusSelect } from "@/components/HospitalizationPermits/StatusSelect";
import { ActionButton } from "@/components/HospitalizationPermits/ActionButton";
import { DischargeTypeSelect } from "@/components/Selects/DischargeTypeSelect";
import { IdcsSelect } from "@/components/Selects/IdcsSelect";

type Props = {
	attendance: Attendance;
	setAttendance: (attendance: Attendance) => void;
};

export function FormInternment({ attendance, setAttendance }: Props) {
	const [loading, setLoading] = useState(true);
	const [isEditingInternment, setIsEditingInternment] = useState(false);
	const { Toast } = useSwal();
	const { api } = useApi();

	const transformedAttendance = transformData<Attendance>(attendance, attendanceMap);
	const { internment, activeHospitalizationPermit, decease, bed } = transformedAttendance;

	const dateOfHospitalization = internment?.dateTimeAdmission
		? dayjs(attendance.internment.dateTimeAdmission).format("DD/MM/YYYY")
		: "";

	const dateTimeDeparture = internment?.dateTimeDeparture
		? dayjs(internment.dateTimeDeparture).format("DD/MM/YYYY")
		: "";

	const nursery = bed ? `${bed?.location} | ${bed.type?.name}` : "";

	async function handleUpdateHospitalizationPermit() {
		try {
			setIsEditingInternment(false);

			const formInternment = document.getElementById("form-internment") as HTMLFormElement;
			const formData = new FormData(formInternment);

			const requestData = Object.fromEntries(formData.entries());

			const { data } = await api.put(
				`/hospitalization-permits/${attendance.id}`,
				requestData
			);

			const transformedAttendance = transformData<Attendance>(data, attendanceMap);

			setAttendance(transformedAttendance);

			setIsEditingInternment(false);

			Toast.fire({
				text: "Internamento editado com sucesso.",
				icon: "success",
			});
		} catch (error) {
			console.log(error);

			Toast.fire({
				text: "Não foi possível editar este internamento.",
				icon: "error",
			});
		}
	}

	useEffect(() => {
		if (Object.entries(attendance).length) {
			setLoading(false);
		}
	}, [attendance]);

	const fieldsToVerify = [
		"dateTimeAdmission",
		"primaryIcdCode",
		"name",
		"dateTimeDeparture",
		"secondaryIcdCode",
		"medicalAuditor",
		"dischargeType",
		"number",
		"icd",
	];

	const attedancesValuesToCheck = {
		name: activeHospitalizationPermit?.solicitor?.name,
		dateTimeAdmission: internment?.dateTimeAdmission,
		primaryIcdCode: activeHospitalizationPermit?.primaryIcdCode,
		dateTimeDeparture: internment?.dateTimeDeparture,
		secondaryIcdCode: activeHospitalizationPermit?.secondaryIcdCode,
		medicalAuditor: activeHospitalizationPermit?.medicalAuditor,
		dischargeType: internment?.dischargeType,
		number: activeHospitalizationPermit?.number,
		icd: decease?.icd,
	};

	const hasSomeBlankField = Object.entries(attedancesValuesToCheck).some(
		([key, value]) => fieldsToVerify.includes(String(key)) && !value
	);

	return (
		<Form id="form-internment">
			<div className="p-4">
				<div className="d-flex flex-responsive">
					<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
						<ListOrFormControlUpdate
							id="date-of-hospitalization"
							type="date"
							name="data_hora_admissao"
							label="Data da Internação:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={dateOfHospitalization}
						/>

						<ListOrFormControlUpdate
							id="primary-icd-code"
							name="primary_icd_code"
							label="CID 10 Principal:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={activeHospitalizationPermit?.primaryIcdCode}
						>
							<IdcsSelect inputId="primary-icd-code" name="primary_icd_code" />
						</ListOrFormControlUpdate>

						<ListOrFormControlUpdate
							id="solicitor-name"
							name="nome"
							label="Médico Solicitante:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={activeHospitalizationPermit?.solicitor?.name}
						/>

						<ListOrFormControlUpdate
							id="date-time-departure"
							name="data_hora_saida"
							type="date"
							label="Data de Alta:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={dateTimeDeparture}
						/>

						<ListOrFormControlUpdate
							id="status"
							label="Status:"
							isEditing={isEditingInternment}
							skeleton={loading}
						>
							<StatusSelect inputId="status" />
						</ListOrFormControlUpdate>
					</div>

					<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
						<ListOrFormControlUpdate
							id="bed"
							label="Enfermaria:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={nursery}
						/>

						<ListOrFormControlUpdate
							id="secondary-icd-code"
							label="CID 10 Secundário:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={activeHospitalizationPermit?.secondaryIcdCode}
						>
							<IdcsSelect inputId="secondary-icd-code" name="secondary_icd_code" />
						</ListOrFormControlUpdate>

						<ListOrFormControlUpdate
							id="medical-auditor"
							name="medical_auditor"
							label="Médico Auditor:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={activeHospitalizationPermit?.medicalAuditor}
						/>

						<ListOrFormControlUpdate
							id="discharge-type"
							label="Tipo de Alta:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={DischargeType[internment?.dischargeType]}
						>
							<DischargeTypeSelect inputId="discharge-type" name="tipo_alta" />
						</ListOrFormControlUpdate>
					</div>

					<div className="col-4 responsive-columns d-flex flex-column gap-2">
						<ListOrFormControlUpdate
							id="number"
							name="number"
							label="N° da AIH:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={activeHospitalizationPermit?.number}
						/>

						<ListOrFormControlUpdate
							label="Leito:"
							notEditable
							isEditing={isEditingInternment}
							skeleton={loading}
							data={bed?.number}
						/>

						<ListOrFormControlUpdate
							id="decease-icd"
							name="cid10"
							label="CID 10 Causa da Morte:"
							isEditing={isEditingInternment}
							skeleton={loading}
							data={decease?.icd}
						>
							<IdcsSelect inputId="decease-icd" name="cid10" />
						</ListOrFormControlUpdate>

						<ListOrFormControlUpdate
							id="tag"
							label="Tag"
							isEditing={isEditingInternment}
							skeleton={loading}
						/>
					</div>
				</div>

				<div className="d-flex justify-content-end mt-2">
					{!loading && (
						<ActionButton
							isEditing={isEditingInternment}
							setIsEditing={setIsEditingInternment}
							data={attendance}
							handleUpdateData={handleUpdateHospitalizationPermit}
							hasSomeBlankField={hasSomeBlankField}
						/>
					)}
				</div>
			</div>
		</Form>
	);
}
